import { useState } from 'react';
import { Bell, Download, PlusSquare, Share, X } from 'lucide-react';
import {
  dismissBanner,
  isBannerDismissed,
  isIOS,
  isStandalone,
  promptInstall,
  useCanPromptInstall,
} from '../../lib/pwa.js';
import { enablePush, isPushEnabledLocally, pushPermission } from '../../lib/messaging.js';
import { useIsMobile } from '../../lib/useIsMobile.js';
import { Button } from '../../components/ui/Button.js';
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/Dialog.js';

/** Safari's Share → Add to Home Screen steps — iOS has no install button a page can trigger. */
export function IosInstallDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const steps = [
    {
      icon: Share,
      text: (
        <>
          Tap <strong>Share</strong> in Safari's toolbar.
        </>
      ),
    },
    {
      icon: PlusSquare,
      text: (
        <>
          Scroll down and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
        </>
      ),
    },
    {
      icon: Bell,
      text: (
        <>
          Open <strong>Pulse</strong> from your Home Screen, sign in, and turn on notifications.
        </>
      ),
    },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Install Pulse on your iPhone</DialogTitle>
        <DialogDescription>
          Installing is also what lets iOS send you a notification when you're tagged in a
          discussion (iOS 16.4 or later).
        </DialogDescription>
      </DialogHeader>
      <ol className="mt-4 flex flex-col gap-3">
        {steps.map((step, i) => (
          <li key={i} className="flex items-start gap-3 text-sm">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-soft-fg">
              <step.icon className="h-4 w-4" />
            </span>
            <span className="pt-1.5">{step.text}</span>
          </li>
        ))}
      </ol>
      <Button className="mt-5 h-10 w-full" variant="default" onClick={() => onOpenChange(false)}>
        Got it
      </Button>
    </Dialog>
  );
}

type BannerKind = 'install' | 'ios-install' | 'notifications';

/** Which banner, if any, this device should see. In the installed app the next step is push;
 * in a browser it's installing. */
function bannerFor(canPrompt: boolean): BannerKind | null {
  if (isStandalone()) {
    const permission = pushPermission();
    const alreadyOn = permission === 'granted' && isPushEnabledLocally();
    return permission === 'unsupported' || permission === 'denied' || alreadyOn
      ? null
      : 'notifications';
  }
  if (canPrompt) return 'install';
  return isIOS() ? 'ios-install' : null;
}

const COPY: Record<BannerKind, { text: string; action: string }> = {
  install: { text: 'Install Pulse for one-tap access and notifications.', action: 'Install' },
  'ios-install': {
    text: 'Add Pulse to your Home Screen to get notified when you’re tagged.',
    action: 'How to',
  },
  notifications: {
    text: 'Get a notification when someone tags you in a discussion.',
    action: 'Turn on',
  },
};

/**
 * A slim, dismissible banner under the mobile top bar inviting the next step towards push on a
 * phone: install the app, then allow notifications. Phones only — desktop users install from the
 * address bar or Settings. Dismissal is remembered per device and per step, so dismissing the
 * install banner doesn't also silence the notifications one after installing.
 */
export function InstallBanner() {
  const isMobile = useIsMobile();
  const canPrompt = useCanPromptInstall();
  const [dismissed, setDismissed] = useState<Set<BannerKind>>(
    () => new Set((['install', 'ios-install', 'notifications'] as const).filter(isBannerDismissed)),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showIosSteps, setShowIosSteps] = useState(false);
  // Bumped after a successful action so the banner re-reads install/push state and hides itself.
  const [, setVersion] = useState(0);

  const kind = isMobile ? bannerFor(canPrompt) : null;
  if (!kind || dismissed.has(kind)) {
    return <IosInstallDialog open={showIosSteps} onOpenChange={setShowIosSteps} />;
  }

  function dismiss() {
    dismissBanner(kind!);
    setDismissed((prev) => new Set(prev).add(kind!));
  }

  async function act() {
    setError(null);
    if (kind === 'ios-install') {
      setShowIosSteps(true);
      return;
    }
    setBusy(true);
    try {
      if (kind === 'install') {
        await promptInstall();
      } else {
        const result = await enablePush();
        if (!result.ok) setError(result.reason);
      }
    } finally {
      setBusy(false);
      setVersion((v) => v + 1);
    }
  }

  const Icon = kind === 'notifications' ? Bell : Download;
  return (
    <>
      <div
        role="region"
        aria-label="Install the app"
        className="flex flex-shrink-0 items-center gap-3 border-b border-border bg-accent-soft px-4 py-2 text-accent-soft-fg md:hidden"
      >
        <Icon className="h-4 w-4 shrink-0" />
        <p className="min-w-0 flex-1 text-[13px] leading-snug">{error ?? COPY[kind].text}</p>
        <Button
          size="sm"
          variant="default"
          className="h-8 shrink-0"
          disabled={busy}
          onClick={() => void act()}
        >
          {busy ? 'Working…' : COPY[kind].action}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-md hover:bg-[color-mix(in_srgb,currentColor_10%,transparent)]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <IosInstallDialog open={showIosSteps} onOpenChange={setShowIosSteps} />
    </>
  );
}
